import React, { useState } from "react";
import { BsDisplay } from "react-icons/bs";
import { FiLogOut } from "react-icons/fi";
import { updateApplicant } from "./applicantApi"; // or wherever you put the file
function StatusBanner({ error, success, successMsg = "Saved!" }) {
  if (!error && !success) return null;
  return (
    <div
      className={`mx-6 mb-3 px-4 py-2 rounded-lg text-sm font-medium ${
        error
          ? "bg-red-50 text-red-700 border border-red-200"
          : "bg-green-50 text-green-700 border border-green-200"
      }`}
    >
      {error || successMsg}
    </div>
  );
}

function Toggle({ active, onToggle }) {
  return (
    <div
      onClick={onToggle}
      className={`w-10 h-5 rounded-full shadow-sm relative transition-colors duration-300 ease-in-out ${
        active ? "bg-[#19BFB7]" : "bg-gray-100"
      } px-[3px] cursor-pointer`}
    >
      <span
        className={`rounded-full w-3 h-3 transition-transform duration-200 bg-white ${
          active ? "translate-x-5" : "translate-x-0"
        } shadow-sm absolute -translate-y-1/2 top-1/2`}
      />
    </div>
  );
}

const PRIVACY_OPTIONS = [
  { title: "Resume",                    key: "Resume" },
  { title: "Work Experience",           key: "WorkExperience" },
  { title: "Education",                 key: "Education" },
  { title: "Licenses and Certifications", key: "LicensesAndCertifications" },
  { title: "Skills",                    key: "Skills" },
  { title: "Socials",                   key: "Socials" },
];

const INITIAL_PRIVACY = Object.fromEntries(PRIVACY_OPTIONS.map((o) => [o.key, false]));

function CandidateEditPrivacy({ applicantId }) {
  // ── Privacy visibility toggles ──────────────────────────────────────────
  const [privacySettings, setPrivacySettings] = useState(INITIAL_PRIVACY);
  const privacySave = updateApplicant(applicantId);

  const togglePrivacy = (key) =>
    setPrivacySettings((prev) => ({ ...prev, [key]: !prev[key] }));

  const handlePrivacySave = () => {
    privacySave.save({
      form_answers: JSON.stringify({ privacy_settings: privacySettings }),
    });
  };

  // ── Password form (needs a separate auth/password-change endpoint) ───────
  const [formData, setFormData]     = useState({ current: "", newPass: "", confirm: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const setField = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handlePasswordSave = async () => {
    setPasswordError("");
    setPasswordSuccess(false);

    if (formData.current.length < 8) {
      setPasswordError("Current password must be at least 8 characters.");
      return;
    }
    if (formData.newPass.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (formData.newPass !== formData.confirm) {
      setPasswordError("New passwords do not match.");
      return;
    }

    // TODO: replace with your password-change endpoint, e.g.:
    // await axios.post("/api/auth/change-password/", {
    //   current_password: formData.current,
    //   new_password:     formData.newPass,
    // }, { headers: { Authorization: `Bearer ${token}` } });

    setPasswordLoading(true);
    try {
      await new Promise((r) => setTimeout(r, 800)); // remove when real call added
      setPasswordSuccess(true);
      setFormData({ current: "", newPass: "", confirm: "" });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch {
      setPasswordError("Failed to change password. Please try again.");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <section className="flex flex-col gap-10">

      {/* ── Password Card ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-4 gap-x-8">
        <div className="min-w-[200px] max-w-[280px] flex flex-1 flex-col">
          <span className="text-[#414651] text-sm font-semibold">Password</span>
          <p className="self-stretch text-[#535862] text-sm">
            Please enter your current password to change your password.
          </p>
        </div>

        <div className="min-w-[320px] max-w-[720px] flex flex-1 flex-col bg-white border border-[#E9EAEB] rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
          <div className="flex self-stretch flex-col gap-6 p-6">

            {/* Current password */}
            <div className="w-full pb-8 border-b border-gray-200 flex items-start">
              <label className="text-[#414651] text-sm font-medium w-1/2">
                Current password <span className="text-[#009C97]">*</span>
              </label>
              <div className="flex flex-col gap-1 w-full">
                <input
                  type="password"
                  value={formData.current}
                  onChange={setField("current")}
                  className="w-full p-2 rounded-lg border border-gray-200 outline-none text-gray-700"
                />
                {formData.current.length > 0 && formData.current.length < 8 && (
                  <p className="text-red-500 text-xs mt-1">
                    Must be at least 8 characters.
                  </p>
                )}
              </div>
            </div>

            {/* New password */}
            <div className="w-full pb-8 border-b border-gray-200 flex items-start">
              <label className="text-[#414651] text-sm font-medium w-1/2">
                New password <span className="text-[#009C97]">*</span>
              </label>
              <div className="flex flex-col gap-1 w-full">
                <input
                  type="password"
                  value={formData.newPass}
                  onChange={setField("newPass")}
                  className="w-full p-2 rounded-lg border border-gray-200 outline-none text-gray-700"
                />
                {formData.newPass.length > 0 && formData.newPass.length < 8 && (
                  <p className="text-red-500 text-xs mt-1">
                    Must be at least 8 characters.
                  </p>
                )}
              </div>
            </div>

            {/* Confirm password */}
            <div className="w-full flex items-start">
              <label className="text-[#414651] text-sm font-medium w-1/2">
                Confirm new password <span className="text-[#009C97]">*</span>
              </label>
              <div className="flex flex-col gap-1 w-full">
                <input
                  type="password"
                  value={formData.confirm}
                  onChange={setField("confirm")}
                  className="w-full p-2 rounded-lg border border-gray-200 outline-none text-gray-700"
                />
                {formData.confirm.length > 0 && formData.confirm !== formData.newPass && (
                  <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
                )}
              </div>
            </div>
          </div>

          <StatusBanner
            error={passwordError}
            success={passwordSuccess}
            successMsg="Password updated successfully!"
          />

          <div className="flex self-stretch items-center flex-col gap-4 pb-4 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-end w-full gap-3 px-6">
              <button
                onClick={handlePasswordSave}
                disabled={passwordLoading}
                className="bg-[#009C97] text-white rounded-lg px-3 py-1.5 cursor-pointer font-semibold text-sm disabled:opacity-60"
              >
                {passwordLoading ? "Saving…" : "Save changes"}
              </button>
              <button
                onClick={() => setFormData({ current: "", newPass: "", confirm: "" })}
                className="text-gray-700 border border-gray-300 hover:bg-gray-100 transition duration-150 rounded-lg px-3 py-1.5 cursor-pointer font-semibold text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Privacy Visibility Card ────────────────────────────────────────── */}
      <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-4 gap-x-8">
        <div className="min-w-[200px] max-w-[280px] flex flex-1 flex-col">
          <span className="text-[#414651] text-sm font-semibold">Privacy</span>
          <p className="self-stretch text-[#535862] text-sm">
            What can recruiters see on your profile?
          </p>
        </div>

        <div className="min-w-[320px] max-w-[720px] flex flex-1 flex-col bg-white border border-[#E9EAEB] rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
          <div className="flex self-stretch flex-col gap-6 p-6">
            {PRIVACY_OPTIONS.map(({ title, key }) => (
              <div key={key} className="flex items-center w-full">
                <h2 className="text-gray-800 text-sm font-semibold w-1/2">{title}</h2>
                <div className="flex items-center gap-3">
                  <Toggle
                    active={privacySettings[key]}
                    onToggle={() => togglePrivacy(key)}
                  />
                  <p className="text-gray-800 text-sm font-semibold">
                    {privacySettings[key] ? "Yes" : "No"}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <StatusBanner
            error={privacySave.error}
            success={privacySave.success}
            successMsg="Privacy settings saved!"
          />

          <div className="flex self-stretch items-center flex-col gap-4 pb-4 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-end w-full gap-3 px-6">
              <button
                onClick={handlePrivacySave}
                disabled={privacySave.loading}
                className="bg-[#009C97] text-white rounded-lg px-3 py-1.5 cursor-pointer font-semibold text-sm disabled:opacity-60"
              >
                {privacySave.loading ? "Saving…" : "Save changes"}
              </button>
              <button
                onClick={() => setPrivacySettings(INITIAL_PRIVACY)}
                className="text-gray-700 border border-gray-300 hover:bg-gray-100 transition duration-150 rounded-lg px-3 py-1.5 cursor-pointer font-semibold text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Active Sessions Card (display only) ───────────────────────────── */}
      <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-4 gap-x-8">
        <div className="min-w-[200px] max-w-[280px] flex flex-1 flex-col">
          <span className="text-[#414651] text-sm font-semibold">Where you're logged in</span>
          <p className="self-stretch text-[#535862] text-sm">
            We'll alert you via olivia@untitledui.com if there is any unusual
            activity on your account.
          </p>
        </div>

        <div className="min-w-[320px] p-6 max-w-[720px] flex gap-6 flex-1 flex-col bg-white border border-[#E9EAEB] rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
          <div className="flex items-start justify-start gap-4 w-full border-b border-gray-200 pb-6">
            <BsDisplay className="w-7 h-7 text-gray-800" />
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <h2 className="text-sm text-gray-700 font-semibold">2024 MacBook Pro 14-inch</h2>
                <div className="border border-gray-200 rounded-lg px-2 py-1 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-600" />
                  <span className="text-gray-600 text-xs font-semibold">Active now</span>
                </div>
              </div>
              <p className="flex max-[420px]:flex-col max-[420px]:items-start items-center gap-2 text-gray-700">
                <span className="text-sm">Melbourne, Australia</span>
                <span className="max-[420px]:hidden">&bull;</span>
                <span className="text-sm">22 Jan at 10:40am</span>
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between w-full">
            <div className="flex items-start justify-start gap-4 w-2/3">
              <BsDisplay className="w-7 h-7 text-gray-800" />
              <div className="flex flex-col gap-2">
                <h2 className="text-sm text-gray-700 font-semibold">2024 MacBook Pro 14-inch</h2>
                <p className="flex max-[420px]:flex-col max-[420px]:items-start items-center gap-2 text-gray-700">
                  <span className="text-sm">Melbourne, Australia</span>
                  <span className="max-[420px]:hidden">&bull;</span>
                  <span className="text-sm">22 Jan at 10:40am</span>
                </p>
              </div>
            </div>
            {/* TODO: call your session revocation endpoint on click */}
            <button className="text-red-600 flex items-center gap-2 cursor-pointer font-semibold text-sm px-3">
              <FiLogOut className="w-5 h-6" />
              Sign out
            </button>
          </div>
        </div>
      </div>

    </section>
  );
}

export default CandidateEditPrivacy;
